import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "./api";

/**
 * SettingsContext — loads public settings (org_name, date_format) on mount.
 * Provides a `formatDate` helper that respects the configured format.
 */
const SettingsContext = createContext({
  settings: { date_format: "uk", org_name: "Stourbridge Scare Maze" },
  refresh: () => {},
  formatDate: (iso, style) => "",
  formatLong: (iso) => "",
  formatFull: (iso) => "",
});

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState({ date_format: "uk", org_name: "Stourbridge Scare Maze" });

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/public/settings`);
      setSettings((s) => ({ ...s, ...data }));
    } catch (e) {
      console.error("Failed to load public settings:", e);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const formatDate = useCallback((iso, style = "short") => formatDateWith(iso, settings.date_format || "uk", style), [settings.date_format]);
  const formatLong = useCallback((iso) => formatDateWith(iso, settings.date_format || "uk", "long"), [settings.date_format]);
  const formatFull = useCallback((iso) => formatDateWith(iso, settings.date_format || "uk", "full"), [settings.date_format]);

  return (
    <SettingsContext.Provider value={{ settings, refresh, formatDate, formatLong, formatFull }}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext);

/**
 * Format an ISO date/timestamp into the configured locale style.
 * - "short": dd/mm/yyyy (uk), mm/dd/yyyy (us), yyyy-mm-dd (iso)
 * - "long": "Mon 12 Oct 2026" (uk/iso) | "Mon, Oct 12, 2026" (us)
 * - "medium": "12 Oct 2026" / "Oct 12, 2026" / "2026-10-12"
 * - "datetime": short date + " HH:mm"
 */
export function formatDateWith(iso, format = "uk", style = "short") {
  if (!iso) return "—";
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (isNaN(d.getTime())) return String(iso);

  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const HH = String(d.getHours()).padStart(2, "0");
  const MM = String(d.getMinutes()).padStart(2, "0");

  if (style === "short") {
    if (format === "us") return `${mm}/${dd}/${yyyy}`;
    if (format === "iso") return `${yyyy}-${mm}-${dd}`;
    return `${dd}/${mm}/${yyyy}`; // uk default
  }
  if (style === "medium") {
    const locale = format === "us" ? "en-US" : "en-GB";
    if (format === "iso") return `${yyyy}-${mm}-${dd}`;
    return d.toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" });
  }
  if (style === "long") {
    const locale = format === "us" ? "en-US" : "en-GB";
    if (format === "iso") return `${yyyy}-${mm}-${dd}`;
    return d.toLocaleDateString(locale, { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
  }
  if (style === "full") {
    // Always a friendly long form with no comma: "Friday 17 October 2026"
    const locale = format === "us" ? "en-US" : "en-GB";
    const weekday = d.toLocaleDateString(locale, { weekday: "long" });
    const month = d.toLocaleDateString(locale, { month: "long" });
    return `${weekday} ${d.getDate()} ${month} ${d.getFullYear()}`;
  }
  if (style === "datetime") {
    return `${formatDateWith(iso, format, "short")} ${HH}:${MM}`;
  }
  if (style === "weekday-day") {
    const locale = format === "us" ? "en-US" : "en-GB";
    return d.toLocaleDateString(locale, { weekday: "short", day: "2-digit", month: "short" });
  }
  return String(iso);
}
